// src/screens/AnalyticsGrowthScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import downIcon from '../assets/dropdown-icon.png';

// ✅ Official icons (same ones used in AnalyticsPostPreviewCard)
import likeIconRed from '../assets/like-icon-red.png';
import commentIcon from '../assets/comment-icon.png';
import repostIcon from '../assets/repost-icon.png';
import saveIconFilled from '../assets/save-icon-filled.png';
import tipIcon from '../assets/tip-icon.png';

import DateRangeDrawer, { labelForRange } from '../components/popups/DateRangeDrawer';

import './AnalyticsGrowthScreen.css';

function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function safeArr(a) {
  return Array.isArray(a) ? a : [];
}

function fmtPi(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return x.toFixed(6).replace(/\.?0+$/, '');
}

// ✅ Returns: { pctText, subText, tone }
// tone: 'pos' | 'neg' | 'muted'
function changeMeta({ current, prev, range }) {
  const curr = safeNum(current);
  const p = safeNum(prev);

  const subText =
    range === 'today' || range === '1d'
      ? 'from yesterday'
      : range === '7d'
        ? 'from previous 7 days'
        : range === '30d'
          ? 'from previous 30 days'
          : '';

  // range=all => don’t show
  if (!subText) return { pctText: '', subText: '', tone: 'muted' };

  // both zero => —
  if (p === 0 && curr === 0) return { pctText: '—', subText, tone: 'muted' };

  // new activity vs 0
  if (p === 0 && curr !== 0) return { pctText: '—', subText, tone: 'muted' };

  const pct = ((curr - p) / p) * 100;
  if (!Number.isFinite(pct)) return { pctText: '—', subText, tone: 'muted' };

  const rounded = Math.round(pct);
  const sign = rounded > 0 ? '+' : '';
  const tone = rounded > 0 ? 'pos' : rounded < 0 ? 'neg' : 'muted';

  return { pctText: `${sign}${rounded}%`, subText, tone };
}

// ✅ Followers can go up or down; use abs(prev) for clean % change
function changeMetaAbsPrev({ current, prev, range }) {
  const curr = safeNum(current);
  const p = safeNum(prev);

  const subText =
    range === 'today' || range === '1d'
      ? 'from yesterday'
      : range === '7d'
        ? 'from previous 7 days'
        : range === '30d'
          ? 'from previous 30 days'
          : '';

  if (!subText) return { pctText: '', subText: '', tone: 'muted' };

  if (p === 0 && curr === 0) return { pctText: '—', subText, tone: 'muted' };
  if (p === 0 && curr !== 0) return { pctText: '—', subText, tone: 'muted' };

  const denom = Math.abs(p);
  const pct = ((curr - p) / denom) * 100;
  if (!Number.isFinite(pct)) return { pctText: '—', subText, tone: 'muted' };

  const rounded = Math.round(pct);
  const sign = rounded > 0 ? '+' : '';
  const tone = rounded > 0 ? 'pos' : rounded < 0 ? 'neg' : 'muted';

  return { pctText: `${sign}${rounded}%`, subText, tone };
}

export default function AnalyticsGrowthScreen({ user }) {
  const navigate = useNavigate();

  const [range, setRange] = useState('7d'); // 'today' | '7d' | '30d' | 'all'
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null); // { planId, message }
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const title = 'Growth';
  const rangeLabel = useMemo(() => labelForRange(range), [range]);

  const goBack = () => {
    if (window.history.length > 1) return navigate(-1);
    return navigate('/analytics');
  };

  const load = async (nextRange) => {
    setLoading(true);
    setError('');
    setBlocked(null);

    try {
      const res = await api.get(`/analytics/me/growth?range=${nextRange}`);
      setData(res?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      const payload = e?.response?.data;

      if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
        setBlocked({
          planId: payload?.planId || 'FREE',
          message:
            payload?.message ||
            'Analytics is available on Premium and Premium+ plans.',
        });
        setData(null);
      } else if (status === 401) {
        setError('Please login again.');
      } else {
        setError(payload?.message || 'Failed to load growth recap.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await load(range);
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const growth = data?.growth || {};
  const totals = growth?.totals || {};
  const series = safeArr(growth?.series);

  const compare = growth?.compare || null;

  const best = growth?.bestDayOfWeek || {};
  const bestPosting = best?.posting;
  const bestEngagement = best?.engagement;
  const bestTips = best?.tips;

  // Best days (from series)
  const bestSeriesPosts = series.reduce(
    (acc, d) => (safeNum(d.posts) > safeNum(acc.posts) ? d : acc),
    { posts: 0 }
  );
  const bestSeriesEng = series.reduce(
    (acc, d) =>
      safeNum(d.engagementTotal) > safeNum(acc.engagementTotal) ? d : acc,
    { engagementTotal: 0 }
  );
  const bestSeriesTips = series.reduce(
    (acc, d) => (safeNum(d.tipsTotalPi) > safeNum(acc.tipsTotalPi) ? d : acc),
    { tipsTotalPi: 0 }
  );

  const totalPosts = safeNum(totals.posts);
  const totalLikes = safeNum(totals.likes);
  const totalComments = safeNum(totals.comments);
  const totalShares = safeNum(totals.shares);
  const totalSaves = safeNum(totals.saves);
  const totalEng = safeNum(totals.engagementTotal);
  const totalTipsCount = safeNum(totals.tipsCount);
  const totalTipsPi = fmtPi(totals.tipsTotalPi);

  // ✅ Followers change in this range
  const followersCurrent = safeNum(compare?.followersNet?.current);

  // ✅ % changes
  const engChange = compare?.engagementTotal
    ? changeMeta({
        current: compare.engagementTotal.current,
        prev: compare.engagementTotal.prev,
        range,
      })
    : { pctText: '', subText: '', tone: 'muted' };

  const tipsChange = compare?.tipsTotalPi
    ? changeMeta({
        current: compare.tipsTotalPi.current,
        prev: compare.tipsTotalPi.prev,
        range,
      })
    : { pctText: '', subText: '', tone: 'muted' };

  const followersChange = compare?.followersNet
    ? changeMetaAbsPrev({
        current: compare.followersNet.current,
        prev: compare.followersNet.prev,
        range,
      })
    : { pctText: '', subText: '', tone: 'muted' };

  return (
    <Screen>
      <div className="ag-container">
        <div className="ag-topbar">
          <img src={backIcon} alt="Back" className="ag-back" onClick={goBack} />
          <h2>{title}</h2>
          <div className="ag-spacer" />
        </div>

        {/* ✅ Range dropdown pill */}
        <div className="ag-rangeRow">
          <button
            type="button"
            className="ag-rangeBtn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Change date range"
            disabled={loading}
          >
            <span className="ag-rangeText">{rangeLabel}</span>
            <img
              src={downIcon}
              alt=""
              className="ag-rangeIcon"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </button>
        </div>

        {loading && (
          <div className="ag-loader">
            <div className="ag-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && blocked && (
          <div className="ag-card ag-blocked">
            <h3>Premium feature</h3>
            <p>{blocked.message}</p>
            <div className="ag-blocked-row">
              <span className="ag-pill">Your plan: {blocked.planId}</span>
              <button className="ag-upgrade" onClick={() => navigate('/plans')}>
                Upgrade
              </button>
            </div>
          </div>
        )}

        {!loading && !blocked && error && (
          <div className="ag-card ag-error">
            <h3>Couldn’t load</h3>
            <p>{error}</p>
            <button className="ag-upgrade" onClick={() => load(range)}>
              Retry
            </button>
          </div>
        )}

        {!loading && !blocked && !error && (
          <div className="ag-scroll">
            {/* ✅ Totals */}
            <div className="ag-grid">
              <div className="ag-stat">
                <div className="ag-stat-label">Posts</div>
                <div className="ag-stat-value">{totalPosts}</div>
              </div>

              <div className="ag-stat">
                <div className="ag-stat-label">Engagement</div>
                <div className="ag-stat-value">{totalEng}</div>

                {engChange.pctText ? (
                  <div className="ag-changeRow">
                    <span className={`ag-changePct ag-${engChange.tone}`}>
                      {engChange.pctText}
                    </span>
                    <span className="ag-changeSub">{engChange.subText}</span>
                  </div>
                ) : null}

                <div className="ag-metrics">
                  <div className="ag-metric">
                    <img src={likeIconRed} alt="" className="ag-icon" />
                    <span className="ag-metric-val">{totalLikes}</span>
                  </div>

                  <div className="ag-metric">
                    <img src={commentIcon} alt="" className="ag-icon" />
                    <span className="ag-metric-val">{totalComments}</span>
                  </div>

                  <div className="ag-metric">
                    <img src={repostIcon} alt="" className="ag-icon" />
                    <span className="ag-metric-val">{totalShares}</span>
                  </div>

                  <div className="ag-metric">
                    <img src={saveIconFilled} alt="" className="ag-icon" />
                    <span className="ag-metric-val">{totalSaves}</span>
                  </div>
                </div>
              </div>

              <div className="ag-stat">
                <div className="ag-stat-label">Tips (Pi)</div>
                <div className="ag-stat-value">{totalTipsPi}</div>

                {tipsChange.pctText ? (
                  <div className="ag-changeRow">
                    <span className={`ag-changePct ag-${tipsChange.tone}`}>
                      {tipsChange.pctText}
                    </span>
                    <span className="ag-changeSub">{tipsChange.subText}</span>
                  </div>
                ) : null}

                <div className="ag-tipSub">
                  <img src={tipIcon} alt="" className="ag-icon" />
                  <span className="ag-tipCount">{totalTipsCount}</span>
                </div>
              </div>

              {/* ✅ Followers (simple) */}
              <div className="ag-stat">
                <div className="ag-stat-label">Followers</div>
                <div className="ag-stat-value">
                  {followersCurrent > 0 ? `+${followersCurrent}` : `${followersCurrent}`}
                </div>

                {followersChange.pctText ? (
                  <div className="ag-changeRow">
                    <span className={`ag-changePct ag-${followersChange.tone}`}>
                      {followersChange.pctText}
                    </span>
                    <span className="ag-changeSub">{followersChange.subText}</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* ✅ Highlights */}
            <div className="ag-card">
              <div className="ag-card-head">
                <h3>Highlights</h3>
              </div>

              <div className="ag-highlights">
                <div className="ag-hl">
                  <div className="ag-hl-title">Best posting day</div>
                  <div className="ag-hl-value">{bestPosting?.label || '—'}</div>
                  <div className="ag-hl-sub">
                    {bestPosting ? `${safeNum(bestPosting.posts)} posts` : '—'}
                  </div>
                </div>

                <div className="ag-hl">
                  <div className="ag-hl-title">Best engagement day</div>
                  <div className="ag-hl-value">{bestEngagement?.label || '—'}</div>
                  <div className="ag-hl-sub">
                    {bestEngagement
                      ? `${safeNum(bestEngagement.engagementTotal)} total`
                      : '—'}
                  </div>
                </div>

                <div className="ag-hl">
                  <div className="ag-hl-title">Best tips day</div>
                  <div className="ag-hl-value">{bestTips?.label || '—'}</div>
                  <div className="ag-hl-sub">
                    {bestTips ? (
                      <span className="ag-hl-tip">
                        <b>{fmtPi(bestTips.tipsTotalPi)} Pi</b>
                        <span className="ag-hl-tipRight">
                          <img src={tipIcon} alt="" className="ag-icon" />
                          <span>{safeNum(bestTips.tipsCount)}</span>
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ Top days in this range */}
            <div className="ag-card">
              <div className="ag-card-head">
                <h3>Top days</h3>
              </div>

              <div className="ag-rows">
                <div className="ag-row">
                  <div className="ag-row-left">
                    <div className="ag-row-title">Most posts</div>
                    <div className="ag-row-sub">{bestSeriesPosts?.date || '—'}</div>
                  </div>
                  <div className="ag-row-right">{safeNum(bestSeriesPosts?.posts)}</div>
                </div>

                <div className="ag-row">
                  <div className="ag-row-left">
                    <div className="ag-row-title">Most engagement</div>
                    <div className="ag-row-sub">{bestSeriesEng?.date || '—'}</div>
                  </div>
                  <div className="ag-row-right">
                    {safeNum(bestSeriesEng?.engagementTotal)}
                  </div>
                </div>

                <div className="ag-row">
                  <div className="ag-row-left">
                    <div className="ag-row-title">Most tips</div>
                    <div className="ag-row-sub">{bestSeriesTips?.date || '—'}</div>
                  </div>
                  <div className="ag-row-right">
                    <span className="ag-row-tip">
                      <b>{fmtPi(bestSeriesTips?.tipsTotalPi)} Pi</b>
                      <span className="ag-row-tipRight">
                        <img src={tipIcon} alt="" className="ag-icon" />
                        <span>{safeNum(bestSeriesTips?.tipsCount)}</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {series.length === 0 && (
                <div className="ag-empty">No activity found for this range.</div>
              )}
            </div>
          </div>
        )}

        {/* ✅ Drawer */}
        <DateRangeDrawer
          open={drawerOpen}
          value={range}
          onClose={() => setDrawerOpen(false)}
          onChange={(v) => setRange(v)}
          title="Date ranges"
        />
      </div>
    </Screen>
  );
}
